import { useState, useEffect } from "react";
import {
  Input,
  Button,
  Card,
  Typography,
  Spin,
  message,
  Descriptions,
  Tag,
  Alert,
  Table,
  Tabs,
  Modal,
} from "antd";
import axios from "axios";
import { toaster } from "evergreen-ui";
import { BASE_URL } from "../../utils/cookies.config";
import moment from "moment";

const { TabPane } = Tabs;

export default function LoyaltyVerification() {
  const [step, setStep] = useState("phone"); // phone, otp, verify
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem("loyaltySession");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setPhoneNumber(session.phoneNumber);
        setVerifiedUser(session.verifiedUser);
        setPrograms(session.programs);
        setStep("verify");
      } catch (e) {
        console.error("Failed to load session:", e);
        localStorage.removeItem("loyaltySession");
      }
    }
  }, []);

  // Save session to localStorage whenever user is verified
  useEffect(() => {
    if (verifiedUser && programs.length > 0) {
      const session = {
        phoneNumber,
        verifiedUser,
        programs,
      };
      localStorage.setItem("loyaltySession", JSON.stringify(session));
    }
  }, [verifiedUser, programs, phoneNumber]);

  // Send OTP
  const handleSendOtp = async () => {
    if (!phoneNumber.trim()) {
      toaster.warning("Please enter your phone number");
      return;
    }

    try {
      setLoading(true);
      await axios.post(BASE_URL + "/loyalty/verification/send-otp", {
        phoneNumber,
      });
      toaster.success("OTP sent to your phone");
      setStep("otp");
    } catch (e) {
      toaster.danger(e.response?.data || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      toaster.warning("Please enter the OTP");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        BASE_URL + "/loyalty/verification/verify-otp",
        {
          phoneNumber,
          otp,
        }
      );

      setVerifiedUser(response.data.user);
      setPrograms(response.data.programs);
      setStep("verify");
      toaster.success("Login successful!");
    } catch (e) {
      toaster.danger(e.response?.data || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // Search for card
  const handleSearchCard = async () => {
    if (!cardNumber.trim()) {
      toaster.warning("Please enter a card number");
      return;
    }

    if (!selectedProgram) {
      toaster.warning("Please select a loyalty program");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        BASE_URL + "/loyalty/verification/search-card",
        {
          cardNumber,
          programId: selectedProgram._id,
        }
      );

      setCardDetails(response.data);
      toaster.success("Card found!");
    } catch (e) {
      toaster.danger(e.response?.data || "Card not found");
      setCardDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Verify card usage
  const handleVerifyCard = async () => {
    if (!cardDetails) {
      toaster.warning("Please search for a card first");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        BASE_URL + "/loyalty/verification/verify-card",
        {
          cardNumber,
          programId: selectedProgram._id,
          verifiedBy: phoneNumber,
        }
      );

      // Show success modal
      setSuccessData({
        message: response.data.message,
        cardDetails: response.data.cardDetails,
        discount: selectedProgram.discount,
        companyName: selectedProgram.companyName,
      });
      setSuccessModalVisible(true);
      
      setCardDetails(null);
      setCardNumber("");
      // Reload verification history after successful verification
      if (selectedProgram) {
        fetchVerificationHistory(selectedProgram._id);
      }
    } catch (e) {
      toaster.danger(e.response?.data || "Failed to verify card");
    } finally {
      setLoading(false);
    }
  };

  // Fetch verification history for a program
  const fetchVerificationHistory = async (programId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/loyalty/programs/verification-history/${programId}`
      );
      
      if (response.data && response.data.history) {
        setVerificationHistory(response.data.history);
      }
    } catch (e) {
      console.error("Failed to fetch verification history:", e);
    }
  };

  // Refresh program details to get latest status
  const refreshProgramDetails = async (programId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/loyalty/programs/one?programId=${programId}`
      );
      
      if (response.data) {
        // Update the program in the programs array
        setPrograms(prevPrograms => 
          prevPrograms.map(p => 
            p._id === programId ? response.data : p
          )
        );
        
        // Update selected program if it's the one being refreshed
        if (selectedProgram?._id === programId) {
          setSelectedProgram(response.data);
        }
      }
    } catch (e) {
      console.error("Failed to refresh program details:", e);
    }
  };

  // Fetch history when program is selected
  useEffect(() => {
    if (selectedProgram?._id) {
      fetchVerificationHistory(selectedProgram._id);
      refreshProgramDetails(selectedProgram._id);
    }
  }, [selectedProgram?._id]);

  // Auto-refresh program details every 30 seconds
  useEffect(() => {
    if (selectedProgram?._id) {
      const interval = setInterval(() => {
        refreshProgramDetails(selectedProgram._id);
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [selectedProgram?._id]);

  // Logout
  const handleLogout = () => {
    setStep("phone");
    setPhoneNumber("");
    setOtp("");
    setCardNumber("");
    setVerifiedUser(null);
    setPrograms([]);
    setSelectedProgram(null);
    setCardDetails(null);
    localStorage.removeItem("loyaltySession");
  };

  return (
    <div style={{ maxWidth: 600, margin: "50px auto", padding: "0 20px" }}>
      {/* Phone Number Step */}
      {step === "phone" && (
        <Card>
          <Typography.Title level={2} style={{ textAlign: "center" }}>
            Loyalty Program Verification
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{ display: "block", textAlign: "center", marginBottom: 20 }}
          >
            Enter your phone number to receive an OTP
          </Typography.Text>

          <Input
            placeholder="Enter phone number (e.g. 0201234567)"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            onPressEnter={handleSendOtp}
            size="large"
            style={{ marginBottom: 16 }}
          />

          <Button
            type="primary"
            onClick={handleSendOtp}
            loading={loading}
            block
            size="large"
          >
            Send OTP
          </Button>
        </Card>
      )}

      {/* OTP Verification Step */}
      {step === "otp" && (
        <Card>
          <Typography.Title level={2} style={{ textAlign: "center" }}>
            Enter OTP
          </Typography.Title>
          <Typography.Text
            type="secondary"
            style={{ display: "block", textAlign: "center", marginBottom: 20 }}
          >
            We sent a code to {phoneNumber}
          </Typography.Text>

          <Input
            placeholder="Enter OTP code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            onPressEnter={handleVerifyOtp}
            size="large"
            style={{ marginBottom: 16 }}
            maxLength={6}
          />

          <Button
            type="primary"
            onClick={handleVerifyOtp}
            loading={loading}
            block
            size="large"
            style={{ marginBottom: 8 }}
          >
            Verify OTP
          </Button>

          <Button type="link" onClick={() => setStep("phone")} block>
            Back to phone number
          </Button>
        </Card>
      )}

      {/* Card Verification Step */}
      {step === "verify" && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  Welcome, {verifiedUser?.name || phoneNumber}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {programs.length} program(s) assigned
                </Typography.Text>
              </div>
              <Button onClick={handleLogout}>Logout</Button>
            </div>
          </Card>

          {programs.length === 0 ? (
            <Alert
              message="No Programs Assigned"
              description="You are not assigned to any loyalty programs. Please contact your administrator."
              type="warning"
              showIcon
            />
          ) : (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Typography.Title level={5}>Select Program</Typography.Title>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {programs.map((program) => (
                    <Card
                      key={program._id}
                      size="small"
                      hoverable
                      onClick={() => setSelectedProgram(program)}
                      style={{
                        border:
                          selectedProgram?._id === program._id
                            ? "2px solid #1890ff"
                            : "1px solid #d9d9d9",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <Typography.Text strong>
                            {program.companyName}
                          </Typography.Text>
                          <br />
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {program.discount}% discount
                          </Typography.Text>
                        </div>
                        {selectedProgram?._id === program._id && (
                          <Tag color="blue">Selected</Tag>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>

              {selectedProgram && (
                <Card>
                  <Tabs defaultActiveKey="verify">
                    <TabPane tab="Verify Cards" key="verify">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          Program Details
                        </Typography.Title>
                        <Button 
                          size="small" 
                          onClick={() => refreshProgramDetails(selectedProgram._id)}
                          icon={<span>🔄</span>}
                        >
                          Refresh
                        </Button>
                      </div>
                      <Descriptions
                        bordered
                        column={1}
                        size="small"
                        style={{ marginBottom: 16 }}
                      >
                        <Descriptions.Item label="Company">
                          {selectedProgram.companyName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Discount">
                          {selectedProgram.discount}%
                        </Descriptions.Item>
                        <Descriptions.Item label="Description">
                          {selectedProgram.description}
                        </Descriptions.Item>
                        {selectedProgram.loyaltyCardType && (
                          <Descriptions.Item label="Card Type">
                            {selectedProgram.loyaltyCardType}
                          </Descriptions.Item>
                        )}
                        {selectedProgram.issuer && (
                          <Descriptions.Item label="Issuer">
                            {selectedProgram.issuer}
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Status">
                          <Tag color={selectedProgram.isActive ? "green" : "red"}>
                            {selectedProgram.isActive ? "Active" : "Inactive"}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>

                      {!selectedProgram.isActive && (
                        <Alert
                          message="Program Deactivated"
                          description="This loyalty program has been deactivated and cannot be used for card verification. You can still view the verification history."
                          type="error"
                          showIcon
                          style={{ marginTop: 16, marginBottom: 16 }}
                        />
                      )}

                      <Typography.Title level={5}>
                        Search Card
                      </Typography.Title>
                      <Input.Search
                        placeholder="Enter card number"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        onSearch={handleSearchCard}
                        enterButton="Search"
                        loading={loading}
                        size="large"
                        style={{ marginBottom: 16 }}
                        disabled={!selectedProgram.isActive}
                      />

                      {cardDetails && (
                        <Card size="small" style={{ marginBottom: 16 }}>
                          <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="Card Holder">
                              {cardDetails.userName || "Unknown"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Card Number">
                              {cardDetails.cardNumber}
                            </Descriptions.Item>

                            <Descriptions.Item label="Status">
                              <Tag
                                color={cardDetails.canUse ? "green" : "red"}
                              >
                                {cardDetails.canUse
                                  ? "Available"
                                  : "Already Used"}
                              </Tag>
                            </Descriptions.Item>
                            {cardDetails.lastUsed && (
                              <Descriptions.Item label="Last Used">
                                {new Date(
                                  cardDetails.lastUsed
                                ).toLocaleString()}
                              </Descriptions.Item>
                            )}
                            {cardDetails.nextAvailableTime && (
                              <Descriptions.Item label="Next Available">
                                {new Date(
                                  cardDetails.nextAvailableTime
                                ).toLocaleString()}
                              </Descriptions.Item>
                            )}
                          </Descriptions>

                          {cardDetails.canUse ? (
                            <Button
                              type="primary"
                              onClick={handleVerifyCard}
                              loading={loading}
                              block
                              size="large"
                              style={{ marginTop: 16 }}
                            >
                              Verify & Apply Discount
                            </Button>
                          ) : (
                            <Alert
                              message="Card Already Used"
                              description={`This card was used recently. Please wait until ${new Date(
                                cardDetails.nextAvailableTime
                              ).toLocaleTimeString()} to use it again.`}
                              type="warning"
                              showIcon
                              style={{ marginTop: 16 }}
                            />
                          )}
                        </Card>
                      )}
                    </TabPane>

                    <TabPane tab="Verification History" key="history">
                      <Typography.Title level={5}>
                        Recent Verifications
                      </Typography.Title>
                      <Table
                        dataSource={verificationHistory}
                        rowKey={(record, index) => index}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        columns={[
                          {
                            title: "Card Number",
                            dataIndex: "cardNumber",
                            key: "cardNumber",
                          },
                          {
                            title: "Card Holder",
                            dataIndex: "cardHolderName",
                            key: "cardHolderName",
                            render: (name) => name || "Unknown",
                          },
                          {
                            title: "Verified By",
                            dataIndex: "verifiedBy",
                            key: "verifiedBy",
                          },
                          {
                            title: "Verified At",
                            dataIndex: "verifiedAt",
                            key: "verifiedAt",
                            render: (date) =>
                              moment(date).format("MMM DD, YYYY hh:mm A"),
                            sorter: (a, b) =>
                              new Date(b.verifiedAt) - new Date(a.verifiedAt),
                            defaultSortOrder: "ascend",
                          },
                        ]}
                      />
                    </TabPane>
                  </Tabs>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {loading && step === "verify" && (
        <div style={{ textAlign: "center", marginTop: 30 }}>
          <Spin size="large" />
        </div>
      )}

      {/* Success Modal */}
      <Modal
        visible={successModalVisible}
        onCancel={() => {
          setSuccessModalVisible(false);
          setSuccessData(null);
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            size="large"
            onClick={() => {
              setSuccessModalVisible(false);
              setSuccessData(null);
            }}
            block
          >
            OK
          </Button>,
        ]}
        centered
        width={400}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
          <Typography.Title level={3} style={{ color: "#52c41a", marginBottom: 8 }}>
            Verification Successful!
          </Typography.Title>
          {successData && (
            <>
              <Typography.Text style={{ fontSize: 16, display: "block", marginBottom: 24 }}>
                {successData.message}
              </Typography.Text>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Company">
                  {successData.companyName}
                </Descriptions.Item>
                <Descriptions.Item label="Card Holder">
                  {successData.cardDetails?.userName}
                </Descriptions.Item>
                <Descriptions.Item label="Discount Applied">
                  <Tag color="green" style={{ fontSize: 16, padding: "4px 12px" }}>
                    {successData.discount}%
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
